import React, { startTransition, useCallback, useContext, useReducer, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { useMediaQuery } from '@/renderer/utils/hooks/useMediaQuery';

import { useEventListener } from './hooks/useEventListener';
import { useInterval } from './hooks/useInterval';
import { IS_SERVER } from './ssr';

// @todo implement integration into service worker

interface IdleContext {
  idle: boolean;
  visible: boolean;
  focused: boolean;
  // Document is no longer in focus, or the user has gone idle
  inactive: boolean;
  lastActive: number;
}

function init(): IdleContext {
  const visible = IS_SERVER ? true : document.visibilityState === 'visible'; // && !document.hidden
  const focused = IS_SERVER ? true : document.hasFocus();

  return {
    idle: false,
    visible,
    focused,
    inactive: !visible || !focused,
    lastActive: Date.now(),
  };
}

const IdleContextStore = React.createContext<IdleContext>(init());

function idleContextReducer<K extends keyof IdleContext>(
  state: IdleContext,
  actions: { type: K; value: IdleContext[K] }[],
) {
  let next: IdleContext | undefined = undefined;

  for (const action of actions) {
    if (state[action.type] !== action.value) {
      if (next === undefined) {
        next = {
          ...state,
          [action.type]: action.value,
        };
      }

      next.inactive = next.idle || !next.visible || !next.focused;
    }
  }

  return next === undefined ? state : next;
}

const IDLE_TIMEOUT = 25000; // longer time to be considered idle on mobile
const IDLE_TIMEOUT_DESKTOP = 10000;

export function IdleContextProvider({ children }: { children: React.ReactNode }) {
  const [context, dispatch] = useReducer(idleContextReducer, init());
  const lastMove = useRef(Date.now());

  if (IS_SERVER) {
    return <IdleContextStore.Provider value={context}>{children}</IdleContextStore.Provider>;
  }

  // biome-ignore lint/correctness/useHookAtTopLevel: SSR
  const documentRef = useRef(document);

  // biome-ignore lint/correctness/useHookAtTopLevel: SSR
  const onVisibilityFull = useCallback(() => {
    const visible = IS_SERVER ? true : document.visibilityState === 'visible'; // && !document.hidden
    const focused = IS_SERVER ? true : document.hasFocus();

    startTransition(() => {
      const patcher = [];

      if (visible && focused) {
        lastMove.current = Date.now();
        patcher.push({ type: 'idle', value: false });
        patcher.push({ type: 'lastActive', value: lastMove.current });
      }

      patcher.push({ type: 'visible', value: visible });
      patcher.push({ type: 'focused', value: focused });

      dispatch(patcher);
    });
  }, []);

  // biome-ignore lint/correctness/useHookAtTopLevel: SSR
  const onVisibility = useDebouncedCallback(onVisibilityFull, 100);

  // biome-ignore lint/correctness/useHookAtTopLevel: SSR
  useEventListener('visibilitychange', onVisibility, documentRef);
  // biome-ignore lint/correctness/useHookAtTopLevel: SSR
  useEventListener('blur', onVisibility);
  // biome-ignore lint/correctness/useHookAtTopLevel: SSR
  useEventListener('focus', onVisibility);

  // biome-ignore lint/correctness/useHookAtTopLevel: SSR
  const onActivityFull = useCallback(() => {
    startTransition(() => {
      lastMove.current = Date.now();
      dispatch([
        { type: 'idle', value: false },
        { type: 'lastActive', value: lastMove.current },
      ]);
    });
  }, []);

  // biome-ignore lint/correctness/useHookAtTopLevel: SSR
  const onActivity = useDebouncedCallback(onActivityFull, 1000);

  // biome-ignore lint/correctness/useHookAtTopLevel: SSR
  useEventListener('mousemove', onActivity);
  // biome-ignore lint/correctness/useHookAtTopLevel: SSR
  useEventListener('click', onActivity);
  // biome-ignore lint/correctness/useHookAtTopLevel: SSR
  useEventListener('keydown', onActivity);
  // biome-ignore lint/correctness/useHookAtTopLevel: SSR
  useEventListener('scroll', onActivity);
  // biome-ignore lint/correctness/useHookAtTopLevel: SSR
  useEventListener('touchstart', onActivity);

  // biome-ignore lint/correctness/useHookAtTopLevel: SSR
  const isDesktop = useMediaQuery('(min-width: 640px)');
  const timeout = isDesktop ? IDLE_TIMEOUT_DESKTOP : IDLE_TIMEOUT;

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  // biome-ignore lint/correctness/useHookAtTopLevel: SSR
  const idleCheck = useCallback(() => {
    if (!context.idle && Date.now() - lastMove.current >= timeout) {
      startTransition(() => {
        dispatch([{ type: 'idle', value: true }]);
        // dispatch({ type: 'idleSince', value: lastMove.current });
      });
    }
  }, [context]);

  // biome-ignore lint/correctness/useHookAtTopLevel: SSR
  useInterval(idleCheck, timeout);

  return <IdleContextStore.Provider value={context}>{children}</IdleContextStore.Provider>;
}

export function useIdleContext() {
  const idleContext = useContext(IdleContextStore);

  return idleContext;
}
